import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { UserManagementDpoModal } from '@app/routes/UserManagement/UserManagementDpoModal';
import { DpoAssignmentModal } from '@components/ui/DpoAssignmentModal/DpoAssignmentModal';
import { useDpoInfoQuery } from '@dieterApi/dpo/useDpoInfoQuery';
import EmailIcon from '@mui/icons-material/Email';
import HomeIcon from '@mui/icons-material/Home';
import PhoneIcon from '@mui/icons-material/Phone';
import WarningIcon from '@mui/icons-material/Warning';
import { Button, Divider, Skeleton } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

export function DpoInfo() {
  const { user, activeCompany } = useUserWithCompany();
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [dpoModalOpen, setDpoModalOpen] = useState(false);

  const { data, loading } = useDpoInfoQuery(activeCompany?.id);
  const dpoInfo = data?.getDpoInfo;

  // Only show for companies with DpoManaged feature
  if (!user?.hasFeature('DpoManaged')) {
    return null;
  }

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col gap-3 mb-2">
        <Skeleton variant="text" width={200} height={32} />
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center gap-4">
            <Skeleton variant="circular" width={64} height={64} />
            <div className="flex-1">
              <Skeleton variant="text" width={150} />
              <Skeleton variant="text" width={100} />
            </div>
          </div>
        </div>
        <Divider className="mt-3!" />
      </div>
    );
  }

  // Generate avatar URL - prefer custom avatar, fallback to generated
  const getAvatarUrl = () => {
    if (dpoInfo?.avatarUrl) {
      return dpoInfo.avatarUrl;
    }
    const displayName = dpoInfo?.name || 'DPO';
    const encoded = encodeURIComponent(displayName);
    // Use the theme's secondary color as the background. It is exposed as the
    // --secondary-root CSS variable and reflects any whitelabel branding.
    // ui-avatars.com expects a bare hex value (no leading '#').
    const secondaryColor = getComputedStyle(document.documentElement)
      .getPropertyValue('--secondary-root')
      .trim()
      .replace(/^#/, '');
    const background = secondaryColor || 'fdd213';
    return `https://ui-avatars.com/api/?name=${encoded}&background=${background}&color=fff&size=128`;
  };

  // Empty state - no DPO assigned
  if (!dpoInfo?.isComplete) {
    return (
      <div className="flex flex-col gap-3 mb-2">
        <div className="flex flex-row justify-between items-center mb-2">
          <span className="text-gray-400 text-xl font-normal">
            {t('dashboard.info.dpo.title', 'Dein Datenschutzbeauftragter')}
          </span>
        </div>

        <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4 flex flex-col gap-3">
          <div className="flex items-center gap-3">
            <WarningIcon className="text-secondary-600" />
            <span className="text-gray-700">{t('dashboard.info.dpo.not_assigned', 'Kein DSB zugewiesen')}</span>
          </div>
          {user?.role === 'Admin' && (
            <Button variant="outlined" size="small" onClick={() => setModalOpen(true)}>
              {t('dashboard.info.dpo.assign_now', 'Jetzt zuweisen')}
            </Button>
          )}
        </div>

        <Divider className="mt-3!" />

        {activeCompany?.id && (
          <DpoAssignmentModal open={modalOpen} onClose={() => setModalOpen(false)} companyId={activeCompany.id} />
        )}
      </div>
    );
  }

  // DPO assigned - show info
  return (
    <div className="flex flex-col gap-3 mb-2">
      <div className="flex flex-row justify-between items-center mb-2">
        <span className="text-gray-400 text-xl font-normal">
          {t('dashboard.info.dpo.title', 'Dein Datenschutzbeauftragter')}
        </span>
      </div>

      <div className="bg-gray-50 rounded-lg p-4 flex flex-col gap-3">
        <div className="flex items-center gap-4">
          <img src={getAvatarUrl()} alt={dpoInfo.name || 'DPO'} className="w-12 h-12 rounded-full object-cover" />
          <div className="flex-1">
            <h3 className="font-semibold text-gray-800">{dpoInfo.name || '-'}</h3>
            <p className="text-sm text-gray-500">{t('dashboard.info.dpo.role', 'Datenschutzbeauftragter')}</p>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          {dpoInfo.email && (
            <a
              href={`mailto:${dpoInfo.email}`}
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-primary-root transition-colors"
            >
              <EmailIcon fontSize="small" className="text-gray-400" />
              <span>{dpoInfo.email}</span>
            </a>
          )}
          {dpoInfo.phone && (
            <a
              href={`tel:${dpoInfo.phone}`}
              className="flex items-center gap-2 text-sm text-gray-700 hover:text-primary-root transition-colors"
            >
              <PhoneIcon fontSize="small" className="text-gray-400" />
              <span>{dpoInfo.phone}</span>
            </a>
          )}
          {dpoInfo.address && (
            <div className="flex items-start gap-2 text-sm text-gray-700">
              <HomeIcon fontSize="small" className="text-gray-400 mt-0.5" />
              <span className="whitespace-pre-line">{dpoInfo.address}</span>
            </div>
          )}
        </div>

        {user?.role === 'Admin' && (
          <Button variant="text" size="small" onClick={() => setModalOpen(true)} className="self-start">
            {t('dashboard.info.dpo.change_assignment', 'Zuweisung ändern')}
          </Button>
        )}
        {(user?.id === dpoInfo.userId || user?.role === 'Admin') && (
          <Button variant="text" size="small" onClick={() => setDpoModalOpen(true)} className="self-start">
            {t('route.user-management.edit-dpo', 'Kontaktdaten bearbeiten')}
          </Button>
        )}
      </div>

      <Divider className="mt-3!" />

      {activeCompany?.id && (
        <DpoAssignmentModal open={modalOpen} onClose={() => setModalOpen(false)} companyId={activeCompany.id} />
      )}
      {dpoInfo.userId && (
        <UserManagementDpoModal
          open={dpoModalOpen}
          onClose={() => setDpoModalOpen(false)}
          userId={dpoInfo.userId}
          initialValues={{
            dpoName: dpoInfo.name,
            dpoEmail: dpoInfo.email,
            dpoPhone: dpoInfo.phone,
            dpoAddress: dpoInfo.address,
            dpoCompany: dpoInfo.company,
            dpoAvatarUrl: dpoInfo.avatarUrl,
          }}
        />
      )}
    </div>
  );
}
